import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import {
    FiPlus,
    FiTrash2,
    FiEdit,
    FiUpload,
    FiX,
    FiSearch,
    FiCheckCircle,
    FiAlertTriangle,
    FiArrowRight,
    FiHash
} from "react-icons/fi";

import {
    doc,
    updateDoc,
    addDoc,
    getFirestore,
    collection,
    getDocs,
    writeBatch,
    serverTimestamp,
    runTransaction
} from "firebase/firestore";

import { useAuth } from "../../component/AuthProvider";

const MySwal = withReactContent(Swal);

const Toast = MySwal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
});

// Firestore batch-ийн 500 write limit-д тулгахгүй байхын тулд нөөцтэй авна.
const FIRESTORE_BATCH_SIZE = 450;
const PLATE_PATTERN = /^\d{4}[А-ЯӨҮ]{3}$/;

// ANPR дээр ашигладагтай ижил төстэй Latin -> Cyrillic normalizer.
const LATIN_TO_CYRILLIC = {
    A: "А",
    B: "В",
    C: "С",
    E: "Е",
    H: "Н",
    K: "К",
    M: "М",
    O: "О",
    P: "Р",
    T: "Т",
    X: "Х",
    Y: "Ү",
    Z: "З",
};

const normalizeText = (value) => String(value ?? "").trim();

const formatPlate = (plate) => {
    if (!plate) return "";

    return String(plate)
        .trim()
        .toUpperCase()
        .replace(/[\s\-·•]/g, "")
        .replace(/[ABCEHKMOPTXYZ]/g, (char) => LATIN_TO_CYRILLIC[char] || char);
};

const isValidPlate = (plate) => PLATE_PATTERN.test(formatPlate(plate));

const readExcelCell = (row, aliases) => {
    for (const key of aliases) {
        if (Object.prototype.hasOwnProperty.call(row, key)) {
            return { present: true, value: row[key] };
        }
    }
    return { present: false, value: undefined };
};

const parseBlockedStatus = (value) => {
    const normalized = normalizeText(value).toLowerCase();
    if (!normalized) return null;

    if (["блоклосон", "blocked", "block", "true", "1", "тийм"].includes(normalized)) {
        return true;
    }

    if (["идэвхтэй", "active", "unblocked", "false", "0", "үгүй"].includes(normalized)) {
        return false;
    }

    return null;
};

const chunkArray = (items, size) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

const Settings = () => {
    const [registeredCars, setRegisteredCars] = useState([]);
    const [searchPlate, setSearchPlate] = useState("");
    const [searchNumber, setSearchNumber] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const { user } = useAuth();

    const pageSize = 100;

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCar, setEditingCar] = useState(null);
    const [formData, setFormData] = useState({
        building: "",
        number: "",
        plate: "",
        desc: "",
        phone: "",
    });

    // Excel Upload States
    const [excelFile, setExcelFile] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState({ newRecords: [], updatedRecords: [] });
    const [previewExcelDuplicates, setPreviewExcelDuplicates] = useState([]);
    const [previewInvalidRows, setPreviewInvalidRows] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const db = getFirestore();
    const auth = getAuth();

    const getCurrentUser = () => auth.currentUser || user || null;

    const getParkingDisplayName = () => {
        const currentUser = getCurrentUser();
        return normalizeText(currentUser?.displayName);
    };

    const showMissingParkingError = () => {
        Toast.fire({
            icon: "error",
            title: "Хэрэглэгчийн displayName олдсонгүй",
        });
    };

    const fetchRegisteredCars = async () => {
        const parkingDisplayName = getParkingDisplayName();
        if (!parkingDisplayName) return;

        const recordsColRef = collection(db, "parkings", parkingDisplayName, "registeredCars");

        try {
            const snapshot = await getDocs(recordsColRef);
            const allCars = snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));
            setRegisteredCars(allCars);
        } catch (error) {
            console.error("Машины мэдээлэл татахад алдаа гарлаа:", error);
            Toast.fire({ icon: "error", title: "Машины мэдээлэл татаж чадсангүй" });
        }
    };

    // AuthProvider хэрэглэгч бэлэн болсны дараа өгөгдлөө татна.
    useEffect(() => {
        const currentUser = auth.currentUser || user;
        if (!currentUser) return;

        if (!normalizeText(currentUser.displayName)) {
            showMissingParkingError();
            return;
        }

        fetchRegisteredCars();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid]);

    const toggleBlockCar = async (car) => {
        const currentUser = getCurrentUser();
        const parkingDisplayName = getParkingDisplayName();
        if (!currentUser || !parkingDisplayName) {
            showMissingParkingError();
            return;
        }

        const result = await MySwal.fire({
            title: car.isBlocked ? "Идэвхжүүлэх үү?" : "Блоклох уу?",
            text: car.isBlocked
                ? "Энэ машин зогсоолд нэвтрэх эрхтэй болно."
                : "Энэ машин зогсоолд нэвтрэх боломжгүй болно.",
            icon: car.isBlocked ? "question" : "warning",
            showCancelButton: true,
            confirmButtonColor: car.isBlocked ? "#10b981" : "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Тийм",
            cancelButtonText: "Цуцлах",
        });

        if (!result.isConfirmed) return;

        try {
            const carRef = doc(
                db,
                "parkings",
                parkingDisplayName,
                "registeredCars",
                car.id
            );

            await updateDoc(carRef, {
                isBlocked: !Boolean(car.isBlocked),
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid || currentUser.email || "unknown",
            });

            // Бүх collection-ийг дахин татахгүйгээр local state-аа шинэчилнэ.
            setRegisteredCars((prev) =>
                prev.map((item) =>
                    item.id === car.id
                        ? { ...item, isBlocked: !Boolean(car.isBlocked) }
                        : item
                )
            );

            Toast.fire({ icon: "success", title: "Амжилттай өөрчиллөө" });
        } catch (error) {
            console.error("Block toggle error:", error);
            MySwal.fire("Алдаа!", "Үйлдэл амжилтгүй боллоо.", "error");
        }
    };

    const openModal = (car = null) => {
        if (car) {
            setEditingCar(car);
            setFormData({
                building: car.building || car.address || "",
                number: car.number || car.apartment || "",
                plate: car.plate || "",
                desc: car.desc || "",
                phone: car.phone || "",
            });
        } else {
            setEditingCar(null);
            setFormData({ building: "", number: "", plate: "", desc: "", phone: "" });
        }

        setIsModalOpen(true);
    };

    const handleSaveCar = async () => {
        const formattedPlate = formatPlate(formData.plate);
        if (!formattedPlate) {
            await MySwal.fire({ icon: "info", title: "Машины дугаар оруулна уу!" });
            return;
        }

        if (!isValidPlate(formattedPlate)) {
            await MySwal.fire({
                icon: "warning",
                title: "Улсын дугаарын формат буруу",
                text: "Жишээ: 5050УБА — 4 цифр + 3 Монгол кирилл үсэг",
            });
            return;
        }

        const isDuplicate = registeredCars.some((car) => {
            const existingPlate = formatPlate(car.plate);
            return editingCar
                ? existingPlate === formattedPlate && car.id !== editingCar.id
                : existingPlate === formattedPlate;
        });

        if (isDuplicate) {
            await MySwal.fire({
                icon: "warning",
                title: `"${formattedPlate}" дугаар бүртгэлтэй байна!`,
            });
            return;
        }

        const currentUser = getCurrentUser();
        const parkingDisplayName = getParkingDisplayName();
        if (!currentUser || !parkingDisplayName) {
            showMissingParkingError();
            return;
        }

        const colRef = collection(db, "parkings", parkingDisplayName, "registeredCars");
        const cleanData = {
            building: normalizeText(formData.building),
            number: normalizeText(formData.number),
            plate: formattedPlate,
            desc: normalizeText(formData.desc),
            phone: normalizeText(formData.phone),
        };

        try {
            if (editingCar) {
                const carRef = doc(colRef, editingCar.id);
                await updateDoc(carRef, {
                    ...cleanData,
                    // Засвар хийхэд өмнөх block төлөвийг хадгална.
                    isBlocked: Boolean(editingCar.isBlocked),
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUser.uid || currentUser.email || "unknown",
                });

                setRegisteredCars((prev) =>
                    prev.map((item) =>
                        item.id === editingCar.id
                            ? { ...item, ...cleanData, isBlocked: Boolean(editingCar.isBlocked) }
                            : item
                    )
                );

                Toast.fire({ icon: "success", title: "Амжилттай шинэчиллээ!" });
            } else {
                const newDocRef = await addDoc(colRef, {
                    ...cleanData,
                    isBlocked: false,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser.uid || currentUser.email || "unknown",
                });

                setRegisteredCars((prev) => [
                    ...prev,
                    {
                        id: newDocRef.id,
                        ...cleanData,
                        isBlocked: false,
                    },
                ]);

                Toast.fire({ icon: "success", title: "Амжилттай нэмэгдлээ!" });
            }

            setIsModalOpen(false);
            setEditingCar(null);
        } catch (error) {
            console.error("Save car error:", error);
            MySwal.fire({ icon: "error", title: "Алдаа гарлаа" });
        }
    };

    const handleDelete = async (car) => {
        const currentUser = getCurrentUser();
        const parkingDisplayName = getParkingDisplayName();
        if (!currentUser || !parkingDisplayName || !car?.id) {
            showMissingParkingError();
            return;
        }

        const result = await MySwal.fire({
            title: "Устгах уу?",
            html: `<b>${car.plate || "Энэ машин"}</b> бүртгэлийг устгах гэж байна.<br/><span style="font-size:12px;color:#64748b">Устгасан мэдээлэл логт хадгалагдана.</span>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Устгах",
            cancelButtonText: "Цуцлах",
        });

        if (!result.isConfirmed) return;

        const carRef = doc(
            db,
            "parkings",
            parkingDisplayName,
            "registeredCars",
            car.id
        );

        // Устгалын логийг үндсэн бүртгэлтэй нэг transaction-д хадгална.
        // Ингэснээр log амжилтгүй бол машин устахгүй, машин устсан бол log заавал үлдэнэ.
        const deletionLogRef = doc(
            collection(db, "parkings", parkingDisplayName, "deletedRegisteredCars")
        );

        try {
            await runTransaction(db, async (transaction) => {
                const latestCarSnapshot = await transaction.get(carRef);

                if (!latestCarSnapshot.exists()) {
                    throw new Error("CAR_NOT_FOUND");
                }

                const latestCarData = latestCarSnapshot.data();

                transaction.set(deletionLogRef, {
                    action: "delete",
                    sourceCollection: "registeredCars",
                    originalDocId: car.id,
                    plate: latestCarData.plate || car.plate || "",
                    carData: latestCarData,
                    deletedAt: serverTimestamp(),
                    deletedBy: {
                        uid: currentUser.uid || "",
                        email: currentUser.email || "",
                        displayName: currentUser.displayName || "",
                    },
                });

                transaction.delete(carRef);
            });

            setRegisteredCars((prev) => prev.filter((item) => item.id !== car.id));
            Toast.fire({ icon: "success", title: "Устгагдлаа" });
        } catch (error) {
            console.error("Delete error:", error);

            if (error?.message === "CAR_NOT_FOUND") {
                setRegisteredCars((prev) => prev.filter((item) => item.id !== car.id));
                Toast.fire({ icon: "warning", title: "Бүртгэл өмнө нь устсан байна" });
                return;
            }

            Toast.fire({ icon: "error", title: "Устгаж чадсангүй" });
        }
    };

    // --- EXCEL LOGIC ---
    const handleFileSelect = (event) => {
        const file = event.target.files?.[0] || null;
        if (!file) return;

        const lowerName = file.name.toLowerCase();
        if (!lowerName.endsWith(".xlsx") && !lowerName.endsWith(".xls")) {
            setExcelFile(null);
            event.target.value = "";
            Toast.fire({ icon: "warning", title: "Зөвхөн Excel файл сонгоно уу" });
            return;
        }

        setExcelFile(file);
        setPreviewExcelDuplicates([]);
        setPreviewInvalidRows([]);
    };

    const handleAnalyzeExcel = () => {
        if (!excelFile) return;

        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const buffer = event.target?.result;
                if (!buffer) throw new Error("Excel файл уншигдсангүй");

                const workbook = XLSX.read(buffer, { type: "array" });
                const firstSheetName = workbook.SheetNames?.[0];
                if (!firstSheetName) throw new Error("Excel sheet олдсонгүй");

                const worksheet = workbook.Sheets[firstSheetName];
                const rawData = XLSX.utils.sheet_to_json(worksheet, {
                    defval: "",
                    raw: false,
                });

                if (!rawData.length) {
                    Toast.fire({ icon: "info", title: "Excel файл хоосон байна" });
                    return;
                }

                processExcelData(rawData);
            } catch (error) {
                console.error("Excel parse error:", error);
                MySwal.fire({
                    icon: "error",
                    title: "Excel файл уншихад алдаа гарлаа",
                    text: "Файлын бүтэц болон өргөтгөлийг шалгана уу.",
                });
            }
        };

        reader.onerror = () => {
            MySwal.fire({ icon: "error", title: "Excel файл уншиж чадсангүй" });
        };

        reader.readAsArrayBuffer(excelFile);
    };

    const processExcelData = (data) => {
        const newRecords = [];
        const updatedRecords = [];
        const invalidRows = [];

        const existingPlatesMap = new Map(
            registeredCars.map((car) => [formatPlate(car.plate), car])
        );

        const excelPlateCount = new Map();
        const firstRowByPlate = new Map();

        data.forEach((row, index) => {
            const plateCell = readExcelCell(row, [
                "plate",
                "Plate",
                "PLATE",
                "Дугаар",
                "дугаар",
                "ДУГААР",
                "dugaar",
            ]);

            const rawPlate = plateCell.present ? plateCell.value : "";
            const formattedPlate = formatPlate(rawPlate);

            if (!formattedPlate || !isValidPlate(formattedPlate)) {
                invalidRows.push({ row: index + 2, value: normalizeText(rawPlate) });
                return;
            }

            excelPlateCount.set(
                formattedPlate,
                (excelPlateCount.get(formattedPlate) || 0) + 1
            );

            if (!firstRowByPlate.has(formattedPlate)) {
                firstRowByPlate.set(formattedPlate, row);
            }
        });

        const duplicates = Array.from(excelPlateCount.entries())
            .filter(([, count]) => count > 1)
            .map(([plate, count]) => ({ plate, count }));

        setPreviewExcelDuplicates(duplicates);
        setPreviewInvalidRows(invalidRows);

        firstRowByPlate.forEach((row, formattedPlate) => {
            const buildingCell = readExcelCell(row, ["building", "Building", "байр", "Байр"]);
            const numberCell = readExcelCell(row, ["number", "Number", "Тоот", "тоот"]);
            const descCell = readExcelCell(row, ["desc", "Description", "Тайлбар", "тайлбар"]);
            const phoneCell = readExcelCell(row, ["phone", "Phone", "Утас", "утас"]);
            const statusCell = readExcelCell(row, ["status", "Status", "Төлөв", "төлөв"]);

            const existingCar = existingPlatesMap.get(formattedPlate);

            if (!existingCar) {
                const parsedStatus = statusCell.present
                    ? parseBlockedStatus(statusCell.value)
                    : null;

                newRecords.push({
                    building: buildingCell.present ? normalizeText(buildingCell.value) : "",
                    number: numberCell.present ? normalizeText(numberCell.value) : "",
                    plate: formattedPlate,
                    desc: descCell.present ? normalizeText(descCell.value) : "",
                    phone: phoneCell.present ? normalizeText(phoneCell.value) : "",
                    isBlocked: parsedStatus ?? false,
                });
                return;
            }

            // Existing record дээр Excel-д БАЙГАА багануудыг л update хийнэ.
            // Багана байхгүй бол хуучин утгыг огт өөрчлөхгүй.
            const changes = {};

            if (buildingCell.present) {
                const value = normalizeText(buildingCell.value);
                if (value !== normalizeText(existingCar.building)) changes.building = value;
            }

            if (numberCell.present) {
                const value = normalizeText(numberCell.value);
                if (value !== normalizeText(existingCar.number)) changes.number = value;
            }

            if (descCell.present) {
                const value = normalizeText(descCell.value);
                if (value !== normalizeText(existingCar.desc)) changes.desc = value;
            }

            if (phoneCell.present) {
                const value = normalizeText(phoneCell.value);
                if (value !== normalizeText(existingCar.phone)) changes.phone = value;
            }

            if (statusCell.present) {
                const parsedStatus = parseBlockedStatus(statusCell.value);
                if (
                    parsedStatus !== null &&
                    parsedStatus !== Boolean(existingCar.isBlocked)
                ) {
                    changes.isBlocked = parsedStatus;
                }
            }

            // Ямар ч бодит өөрчлөлт байхгүй бол unnecessary write хийхгүй.
            if (Object.keys(changes).length > 0) {
                updatedRecords.push({
                    id: existingCar.id,
                    old: existingCar,
                    changes,
                    new: {
                        ...existingCar,
                        ...changes,
                        plate: formattedPlate,
                    },
                });
            }
        });

        setPreviewData({ newRecords, updatedRecords });
        setIsPreviewOpen(true);
    };

    const confirmBatchUpload = async () => {
        const currentUser = getCurrentUser();
        const parkingDisplayName = getParkingDisplayName();
        if (!currentUser || !parkingDisplayName) {
            showMissingParkingError();
            return;
        }

        const operations = [];
        const seenNewPlates = new Set();
        const seenUpdatedIds = new Set();

        previewData.newRecords.forEach((car) => {
            if (seenNewPlates.has(car.plate)) return;
            seenNewPlates.add(car.plate);
            operations.push({ type: "create", car });
        });

        previewData.updatedRecords.forEach((item) => {
            if (seenUpdatedIds.has(item.id)) return;
            if (!item.changes || Object.keys(item.changes).length === 0) return;
            seenUpdatedIds.add(item.id);
            operations.push({ type: "update", item });
        });

        if (operations.length === 0) {
            Toast.fire({ icon: "info", title: "Хадгалах өөрчлөлт алга" });
            return;
        }

        setIsUploading(true);

        try {
            const chunks = chunkArray(operations, FIRESTORE_BATCH_SIZE);

            for (const chunk of chunks) {
                const batch = writeBatch(db);

                chunk.forEach((operation) => {
                    if (operation.type === "create") {
                        const newRef = doc(
                            collection(db, "parkings", parkingDisplayName, "registeredCars")
                        );

                        batch.set(newRef, {
                            ...operation.car,
                            createdAt: serverTimestamp(),
                            createdBy: currentUser.uid || currentUser.email || "unknown",
                        });
                        return;
                    }

                    const item = operation.item;
                    const carRef = doc(
                        db,
                        "parkings",
                        parkingDisplayName,
                        "registeredCars",
                        item.id
                    );

                    batch.update(carRef, {
                        ...item.changes,
                        updatedAt: serverTimestamp(),
                        updatedBy: currentUser.uid || currentUser.email || "unknown",
                    });
                });

                await batch.commit();
            }

            await MySwal.fire({
                title: "Амжилттай!",
                html: `Нэмсэн: <b>${seenNewPlates.size}</b><br/>Шинэчилсэн: <b>${seenUpdatedIds.size}</b>`,
                icon: "success",
                confirmButtonColor: "#3b82f6",
            });

            setIsPreviewOpen(false);
            setExcelFile(null);
            setPreviewData({ newRecords: [], updatedRecords: [] });
            setPreviewExcelDuplicates([]);
            setPreviewInvalidRows([]);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

            await fetchRegisteredCars();
        } catch (error) {
            console.error("Batch upload error:", error);

            // Олон batch-ийн аль нэг нь амжилттай commit болсон байж болох тул
            // local state/preview-г хуучнаар нь дахин commit хийхээс хамгаалж refresh хийнэ.
            await fetchRegisteredCars();
            setIsPreviewOpen(false);
            setPreviewData({ newRecords: [], updatedRecords: [] });

            MySwal.fire({
                title: "Импорт бүрэн дууссангүй",
                text: "Зарим batch хадгалагдсан байж болно. Excel файлаа дахин 'Шалгах' товчоор шинжилж үлдсэн өөрчлөлтийг үргэлжлүүлнэ үү.",
                icon: "error",
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Filter Logic
    const filteredLogs = useMemo(() => {
        const normalizedPlateSearch = formatPlate(searchPlate);
        const normalizedNumberSearch = searchNumber.toLowerCase().replace(/\s/g, "");

        return registeredCars.filter((car) => {
            const matchPlate =
                !normalizedPlateSearch ||
                formatPlate(car.plate).includes(normalizedPlateSearch);

            const normalizedCarNumber = String(
                car.number ?? car.apartment ?? ""
            )
                .toLowerCase()
                .replace(/\s/g, "");

            const matchNumber =
                !normalizedNumberSearch ||
                normalizedCarNumber.includes(normalizedNumberSearch);

            return matchPlate && matchNumber;
        });
    }, [registeredCars, searchPlate, searchNumber]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));

    // Устгал/шүүлтийн дараа currentPage totalPages-ээс хэтэрвэл хоосон хүснэгт гарахаас хамгаална.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(Math.max(prev, 1), totalPages));
    }, [totalPages]);

    const paginatedLogs = filteredLogs.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
    const handleNext = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

    return (
        // ⭐️ Хамгийн гаднах хэсэгт max-w-[100vw] overflow-x-hidden нэмж дэлгэц халихаас сэргийллээ
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-3 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800 tw-max-w-[100vw] tw-overflow-x-hidden">
            
            {/* HEADER */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-6 sm:tw-mb-8">
                <div>
                    <h2 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-m-0">Бүртгэлтэй машинууд</h2>
                    <p className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-mt-1 tw-mb-0">Зогсоолд үнэгүй нэвтрэх эрхтэй оршин суугчид болон ажилчдын бүртгэл</p>
                </div>
            </div>

            {/* CONTROLS (Add Manual & Excel) */}
            <div className="tw-bg-white tw-p-4 sm:tw-p-6 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-mb-6 sm:tw-mb-8 tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 lg:tw-gap-8 lg:tw-divide-x tw-divide-slate-100">
                
                <div className="tw-flex-1">
                    <h4 className="tw-text-sm sm:tw-text-base tw-font-bold tw-text-slate-800 tw-mb-3 sm:tw-mb-4">Гар аргаар нэмэх</h4>
                    <button 
                        onClick={() => openModal()}
                        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full sm:tw-w-auto tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-bold tw-transition-all tw-shadow-sm hover:tw-shadow active:tw-scale-95"
                    >
                        <FiPlus size={18}/> Шинэ машин нэмэх
                    </button>
                </div>

                <div className="tw-flex-1 lg:tw-pl-8">
                    <h4 className="tw-text-sm sm:tw-text-base tw-font-bold tw-text-slate-800 tw-mb-3 sm:tw-mb-4">Excel-ээс хуулах</h4>
                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-3">
                        <div className="tw-relative tw-w-full sm:tw-w-auto">
                            <input
                                ref={fileInputRef}
                                type="file" accept=".xlsx, .xls"
                                onChange={handleFileSelect}
                                className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-opacity-0 tw-cursor-pointer"
                            />
                            <div className="tw-bg-slate-50 tw-border tw-border-dashed tw-border-slate-300 hover:tw-border-blue-400 hover:tw-bg-blue-50 tw-text-slate-600 tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-center sm:tw-justify-start tw-gap-2 tw-w-full">
                                <FiUpload /> {excelFile ? excelFile.name : "Файл сонгох..."}
                            </div>
                        </div>
                        
                        <button
                            onClick={handleAnalyzeExcel}
                            disabled={!excelFile}
                            className={`tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-bold tw-transition-all tw-shadow-sm active:tw-scale-95 ${excelFile ? 'tw-bg-emerald-500 hover:tw-bg-emerald-600 tw-text-white hover:tw-shadow' : 'tw-bg-slate-100 tw-text-slate-400 tw-cursor-not-allowed'}`}
                        >
                            <FiCheckCircle size={18}/> Шалгах & Хуулах
                        </button>
                    </div>
                    <p className="tw-text-xs tw-text-slate-500 tw-mt-3 tw-leading-relaxed">
                        Баганын нэрс: <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">plate</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">building</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">number</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">desc</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">phone</span>
                    </p>
                </div>
            </div>

            {/* SEARCH */}
            <div className="tw-bg-white tw-rounded-t-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-border-b-0 tw-p-4 tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 sm:tw-gap-4">
                <div className="tw-relative tw-w-full sm:tw-w-72">
                    <FiSearch className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input
                        type="text" placeholder="Улсын дугаар..."
                        value={searchPlate} onChange={e => { setSearchPlate(e.target.value); setCurrentPage(1); }}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
                <div className="tw-relative tw-w-full sm:tw-w-48">
                    <FiHash className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input
                        type="text" placeholder="Тоот..."
                        value={searchNumber} onChange={e => { setSearchNumber(e.target.value); setCurrentPage(1); }}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
            </div>

            {/* TABLE - ⭐️ tw-overflow-hidden болон overflow-x-auto нэмэгдсэн */}
            <div className="tw-bg-white tw-shadow-sm tw-border tw-border-slate-200 tw-rounded-b-2xl tw-w-full tw-overflow-hidden">
                <div className="tw-overflow-x-auto tw-w-full tw-block">
                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[700px] md:tw-min-w-[800px]">
                        <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
                            <tr>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-w-12 sm:tw-w-16">№</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Байр / Тоот</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Тоот</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Улсын дугаар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Холбогдох дугаар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Тайлбар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center">Төлөв</th>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-right">Үйлдэл</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                            {paginatedLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="tw-px-6 tw-py-12 tw-text-center tw-text-slate-400">Мэдээлэл олдсонгүй.</td>
                                </tr>
                            ) : paginatedLogs.map((car, idx) => (
                                <tr key={car.id} className="hover:tw-bg-slate-50/80 tw-transition-colors">
                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-400">
                                        {(currentPage - 1) * pageSize + idx + 1}
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-700">
                                        {car.building ? car.building : car.address? `${car.address}`:""} 
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-700">
                                        {car.number ? `  ${car.number}` : car.apartment ? `  ${car.apartment}` : ""}
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4">
                                        <span className="tw-inline-block tw-px-2 sm:tw-px-2.5 tw-py-1 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-md tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-800 tw-tracking-wider">
                                            {car.plate}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">{car.phone || "-"}</td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-truncate tw-max-w-[100px] sm:tw-max-w-[150px]" title={car.desc}>{car.desc || "-"}</td>
                                    
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-center">
                                        {/* Toggle Switch */}
                                        <button 
                                            onClick={() => toggleBlockCar(car)}
                                            className={`tw-relative tw-inline-flex tw-h-5 sm:tw-h-6 tw-w-10 sm:tw-w-11 tw-items-center tw-rounded-full tw-transition-colors focus:tw-outline-none ${!car.isBlocked ? 'tw-bg-emerald-500' : 'tw-bg-rose-500'}`}
                                            title={car.isBlocked ? "Блоклогдсон (Дарж нээх)" : "Идэвхтэй (Дарж блоклох)"}
                                            aria-label={car.isBlocked ? `${car.plate} идэвхжүүлэх` : `${car.plate} блоклох`}
                                        >
                                            <span className={`tw-inline-block tw-h-3.5 sm:tw-h-4 tw-w-3.5 sm:tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition-transform tw-shadow-sm ${!car.isBlocked ? 'tw-translate-x-5 sm:tw-translate-x-6' : 'tw-translate-x-1'}`} />
                                        </button>
                                    </td>

                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4">
                                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 sm:tw-gap-2">
                                            <button type="button" aria-label={`${car.plate} засах`} onClick={() => openModal(car)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-rounded-lg tw-transition-colors">
                                                <FiEdit size={16} />
                                            </button>
                                            <button type="button" aria-label={`${car.plate} устгах`} onClick={() => handleDelete(car)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-rose-600 hover:tw-bg-rose-50 tw-rounded-lg tw-transition-colors">
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {filteredLogs.length > pageSize && (
                    <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3">
                        <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500">Нийт <span className="tw-font-bold tw-text-slate-900">{filteredLogs.length}</span> бичлэг</div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto tw-justify-between sm:tw-justify-end">
                            <button onClick={handlePrev} disabled={currentPage === 1} className="tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">Өмнөх</button>
                            <div className="tw-px-3 sm:tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700">{currentPage} / {totalPages}</div>
                            <button onClick={handleNext} disabled={currentPage === totalPages} className="tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">Дараах</button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- ADD/EDIT MODAL --- */}
            {isModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-slate-900/40 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in tw-duration-200">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden tw-animate-in tw-zoom-in-95 tw-duration-200">
                        <div className="tw-flex tw-justify-between tw-items-center tw-px-5 sm:tw-px-6 tw-py-4 tw-border-b tw-border-slate-100">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-slate-800">{editingCar ? "Машин засах" : "Шинэ машин нэмэх"}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500 tw-transition-colors"><FiX size={24} /></button>
                        </div>
                        <div className="tw-p-5 sm:tw-p-6 tw-space-y-4">
                            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
                                <div className="tw-flex-1">
                                    <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Байрны нэр</label>
                                    <input type="text" value={formData.building} onChange={e => setFormData({ ...formData, building: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Жишээ: 101-р байр" />
                                </div>
                                <div className="tw-flex-1">
                                    <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Тоот</label>
                                    <input type="text" value={formData.number} onChange={e => setFormData({ ...formData, number: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Жишээ: 45" />
                                </div>
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Улсын дугаар <span className="tw-text-rose-500">*</span></label>
                                <input type="text" value={formData.plate} onChange={e => setFormData({ ...formData, plate: e.target.value.toUpperCase() })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-tracking-widest focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="5050УБА" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Холбогдох утас</label>
                                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="99112233" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Тайлбар</label>
                                <input type="text" value={formData.desc} onChange={e => setFormData({ ...formData, desc: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Нэмэлт мэдээлэл..." />
                            </div>
                        </div>
                        <div className="tw-px-5 sm:tw-px-6 tw-py-4 tw-bg-slate-50 tw-border-t tw-border-slate-100 tw-flex tw-justify-end tw-gap-3">
                            <button onClick={() => setIsModalOpen(false)} className="tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-200 tw-rounded-xl tw-transition-colors">Болих</button>
                            <button onClick={handleSaveCar} className="tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-xl tw-shadow-sm tw-transition-colors">Хадгалах</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EXCEL PREVIEW MODAL --- */}
            {isPreviewOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1001] tw-bg-slate-900/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden tw-animate-in tw-slide-in-from-bottom-4">
                        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-bg-slate-800 tw-text-white">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-flex tw-items-center tw-gap-2"><FiUpload /> Excel өгөгдөл шалгах</h3>
                            <button onClick={() => setIsPreviewOpen(false)} className="tw-text-slate-300 hover:tw-text-white"><FiX size={24} /></button>
                        </div>
                        
                        <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 sm:tw-p-6 tw-bg-slate-50 custom-scrollbar">
                            
                            {/* Буруу форматтай мөрүүд */}
                            {previewInvalidRows.length > 0 && (
                                <div className="tw-mb-4 sm:tw-mb-6 tw-bg-white tw-border tw-border-orange-200 tw-rounded-xl tw-overflow-hidden">
                                    <div className="tw-bg-orange-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-orange-100 tw-flex tw-items-center tw-gap-2">
                                        <FiAlertTriangle className="tw-text-orange-500" />
                                        <h4 className="tw-font-bold tw-text-orange-700 tw-text-xs sm:tw-text-sm">Алгассан мөр: {previewInvalidRows.length}</h4>
                                    </div>
                                    <div className="tw-p-3 sm:tw-p-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">
                                        Улсын дугаар нь 4 цифр + 3 Монгол кирилл үсгийн форматтай биш мөрүүдийг импортлоогүй.
                                        {previewInvalidRows.slice(0, 10).map((item) => (
                                            <div key={item.row} className="tw-mt-1 tw-font-medium">
                                                Мөр {item.row}: {item.value || "(хоосон)"}
                                            </div>
                                        ))}
                                        {previewInvalidRows.length > 10 && (
                                            <div className="tw-mt-1 tw-text-slate-400">... болон өөр {previewInvalidRows.length - 10} мөр</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Давхардал */}
                            {previewExcelDuplicates.length > 0 && (
                                <div className="tw-mb-4 sm:tw-mb-6 tw-bg-white tw-border tw-border-rose-200 tw-rounded-xl tw-overflow-hidden">
                                    <div className="tw-bg-rose-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-rose-100 tw-flex tw-items-center tw-gap-2">
                                        <FiAlertTriangle className="tw-text-rose-500" />
                                        <h4 className="tw-font-bold tw-text-rose-700 tw-text-xs sm:tw-text-sm">Excel доторх давхардсан дугаарууд (эхний мөрийг ашиглана)</h4>
                                    </div>
                                    <div className="tw-p-3 sm:tw-p-4 tw-flex tw-flex-wrap tw-gap-2">
                                        {previewExcelDuplicates.map((d, i) => (
                                            <span key={i} className="tw-inline-flex tw-items-center tw-gap-1 sm:tw-gap-1.5 tw-bg-rose-50 tw-border tw-border-rose-200 tw-text-rose-700 tw-px-2 sm:tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold">
                                                {d.plate} <span className="tw-bg-rose-200 tw-text-rose-800 tw-px-1 sm:tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] sm:tw-text-xs">{d.count}х</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Шинэчлэгдэх */}
                            {previewData.updatedRecords.length > 0 && (
                                <div className="tw-mb-4 sm:tw-mb-6 tw-bg-white tw-border tw-border-amber-200 tw-rounded-xl tw-overflow-hidden">
                                    <div className="tw-bg-amber-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-amber-100 tw-flex tw-items-center tw-justify-between">
                                        <h4 className="tw-font-bold tw-text-amber-700 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-gap-2">
                                            <FiAlertTriangle /> Бүртгэлтэй байгааг шинэчлэх
                                        </h4>
                                        <span className="tw-bg-amber-200 tw-text-amber-800 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] sm:tw-text-xs tw-font-bold">{previewData.updatedRecords.length}</span>
                                    </div>
                                    <div className="tw-overflow-x-auto">
                                        <table className="tw-w-full tw-text-left tw-text-xs sm:tw-text-sm">
                                            <thead className="tw-bg-slate-50 tw-text-slate-500">
                                                <tr><th className="tw-px-3 sm:tw-px-4 tw-py-2">Дугаар</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Хуучин</th><th></th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Шинэ</th></tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-slate-100">
                                                {previewData.updatedRecords.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-font-bold">{item.new.plate}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-slate-500 tw-text-[10px] sm:tw-text-xs">
                                                            Байр: {item.old.building}-{item.old.number}<br/>Утас: {item.old.phone}
                                                        </td>
                                                        <td className="tw-px-1 sm:tw-px-2 tw-text-slate-300"><FiArrowRight/></td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-emerald-700 tw-font-medium tw-text-[10px] sm:tw-text-xs">
                                                            Байр: {item.new.building}-{item.new.number}<br/>Утас: {item.new.phone}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Шинээр нэмэгдэх */}
                            <div className="tw-bg-white tw-border tw-border-emerald-200 tw-rounded-xl tw-overflow-hidden">
                                <div className="tw-bg-emerald-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-emerald-100 tw-flex tw-items-center tw-justify-between">
                                    <h4 className="tw-font-bold tw-text-emerald-700 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-gap-2">
                                        <FiCheckCircle /> Шинээр нэмэгдэх
                                    </h4>
                                    <span className="tw-bg-emerald-200 tw-text-emerald-800 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] sm:tw-text-xs tw-font-bold">{previewData.newRecords.length}</span>
                                </div>
                                {previewData.newRecords.length > 0 ? (
                                    <div className="tw-overflow-x-auto">
                                        <table className="tw-w-full tw-text-left tw-text-xs sm:tw-text-sm">
                                            <thead className="tw-bg-slate-50 tw-text-slate-500">
                                                <tr><th className="tw-px-3 sm:tw-px-4 tw-py-2">Дугаар</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Байр/Тоот</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Утас</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Тайлбар</th></tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-slate-100">
                                                {previewData.newRecords.map((car, idx) => (
                                                    <tr key={idx}>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-font-bold">{car.plate}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-600">{car.building}-{car.number}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-600">{car.phone}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-500 tw-truncate tw-max-w-[120px] sm:tw-max-w-[200px]">{car.desc}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="tw-p-4 sm:tw-p-6 tw-text-center tw-text-slate-400 tw-text-xs sm:tw-text-sm">Шинэ машин олдсонгүй.</div>
                                )}
                            </div>
                        </div>
                        
                        <div className="tw-bg-white tw-border-t tw-border-slate-200 tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3 sm:tw-gap-0">
                            <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-w-full sm:tw-w-auto tw-text-center sm:tw-text-left">
                                Нийт хуулах: <span className="tw-font-bold tw-text-slate-900 tw-text-base sm:tw-text-lg">{previewData.newRecords.length + previewData.updatedRecords.length}</span> машин
                            </div>
                            <div className="tw-flex tw-gap-2 sm:tw-gap-3 tw-w-full sm:tw-w-auto">
                                <button onClick={() => setIsPreviewOpen(false)} className="tw-flex-1 sm:tw-flex-none tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-100 tw-transition-colors">Болих</button>
                                <button 
                                    onClick={confirmBatchUpload}
                                    disabled={isUploading || (previewData.newRecords.length === 0 && previewData.updatedRecords.length === 0)}
                                    className="tw-flex-1 sm:tw-flex-none tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-shadow-sm disabled:tw-bg-slate-300 disabled:tw-cursor-not-allowed tw-transition-all"
                                >
                                    {isUploading ? "Хадгалж байна..." : "Баталгаажуулах"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Settings;